<template>
  <div ref="printable" class="chat-transcript">
    <div class="header">
      <div class="title">
        <p class="title-date">{{ printDate }}</p>
        <div>
          <svg
            width="40"
            height="30"
            viewBox="0 0 40 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="32.2727"
              cy="11.8181"
              r="6.95455"
              fill="#FBFBFC"
              stroke="#332C49"
              stroke-width="1.54545"
            />
            <circle
              cx="7.72678"
              cy="11.8182"
              r="6.95455"
              fill="#FBFBFC"
              stroke="#332C49"
              stroke-width="1.54545"
            />
            <circle
              cx="19.9991"
              cy="15"
              r="13.7727"
              fill="#FBFBFC"
              stroke="#332C49"
              stroke-width="1.54545"
            />
            <path
              d="M17.8518 16.2163C17.6323 16.6896 17.3189 17.0592 16.9116 17.3264C16.5043 17.5936 16.0183 17.7272 15.4537 17.7272C14.8974 17.7272 14.4114 17.5936 13.9959 17.3264C13.5886 17.0592 13.2752 16.6896 13.0557 16.2163C12.8362 15.7361 12.7271 15.1753 12.7271 14.5338C12.7271 13.8855 12.8362 13.3247 13.0557 12.8514C13.2752 12.3781 13.5886 12.0119 13.9959 11.7528C14.4114 11.4937 14.8974 11.3635 15.4537 11.3635C16.0183 11.3635 16.5043 11.4937 16.9116 11.7528C17.3189 12.0119 17.6323 12.3781 17.8518 12.8514C18.0713 13.3247 18.1816 13.8855 18.1816 14.5338C18.1804 15.1753 18.0713 15.7361 17.8518 16.2163Z"
              fill="#332C49"
            />
            <path
              d="M26.9425 16.2164C26.723 16.6897 26.4096 17.0594 26.0023 17.3265C25.595 17.5937 25.109 17.7273 24.5444 17.7273C23.9881 17.7273 23.5021 17.5937 23.0866 17.3265C22.6793 17.0594 22.3659 16.6897 22.1464 16.2164C21.9269 15.7362 21.8177 15.1754 21.8177 14.534C21.8177 13.8856 21.9269 13.3248 22.1464 12.8515C22.3659 12.3782 22.6793 12.012 23.0866 11.7529C23.5021 11.4938 23.9881 11.3636 24.5444 11.3636C25.109 11.3636 25.595 11.4938 26.0023 11.7529C26.4096 12.012 26.723 12.3782 26.9425 12.8515C27.162 13.3248 27.2723 13.8856 27.2723 14.534C27.2711 15.1754 27.162 15.7362 26.9425 16.2164Z"
              fill="#332C49"
            />
          </svg>
        </div>
      </div>
      <p class="url">
        <a :href="conversationURL" target="_blank">{{ conversationURL }}</a>
      </p>
    </div>
    <div class="table-wrap">
      <table>
        <tr>
          <th>{{ $t('CONVERSATION.CLIENT') }}</th>
          <th>{{ $t('CREATE_ACCOUNT.FORM.EMAIL.LABEL') }}</th>
          <th>{{ $t('CONVERSATION.START_TIME') }}</th>
          <th>{{ $t('CONVERSATION.STATUS_CONVERSATION') }}</th>
          <th>{{ $t('CONVERSATION.CHANNEL_TYPE') }}</th>
          <th>{{ $t('CONVERSATION.ASSIGNED_AGENT') }}</th>
        </tr>
        <tr>
          <td>{{ headerData.client }}</td>
          <td>{{ headerData.clientEmail }}</td>
          <td>{{ headerData.startTime }}</td>
          <td style="text-transform: capitalize;">
            {{ headerData.status }}
          </td>
          <td>{{ headerData.channel }}</td>
          <td>{{ headerData.agent }}</td>
        </tr>
      </table>
    </div>
    <div v-if="error" class="error">{{ error }}</div>
    <div class="messages-wrapper">
      <div
        v-for="(message, index) in formattedConversations"
        :key="index"
        class="messages"
        :class="{ private: message.isPrivate }"
      >
        <p class="timestamp">
          <strong>{{ message.sender }}</strong>
          <span v-if="message.isBot">(Bot)</span>
          <span class="date-style">[{{ message.timestamp }}]</span>
          <span v-if="message.isPrivate" class="private-indicator">
            Private
          </span>
        </p>
        <div class="content" v-html="message.content" />
        <div
          v-if="message.attachments && message.attachments.length"
          class="attachments"
        >
          <div v-for="(attachment, idx) in message.attachments" :key="idx">
            <img
              v-if="attachment.file_type === 'image'"
              :src="attachment.data_url"
              alt="Attachment"
              class="attachment-image"
            />
            <a
              v-if="attachment.file_type === 'image'"
              :href="attachment.data_url"
              target="_top"
              rel="noopener noreferrer"
              class="attachment-link"
            >
              Open Attachment
            </a>
            <a
              v-else
              :href="attachment.data_url"
              target="_blank"
              rel="noopener noreferrer"
              class="attachment-link"
            >
              Download {{ attachment.file_type || 'Attachment' }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';
import { generateBotMessageContent } from '../helpers/botMessageContentHelper';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';

export default {
  name: 'PrintTemplate',
  mixins: [messageFormatterMixin],
  props: {
    headerData: {
      type: Object,
      required: true,
    },
    conversations: {
      type: Array,
      default: () => [],
      required: true,
    },
  },
  data() {
    return {
      error: null,
      hasPrivate: false,
      md: new MarkdownIt({
        html: true,
        linkify: true,
        typographer: true,
      }),
    };
  },
  computed: {
    conversationURL() {
      return `${window.location.origin}${this.$route.path}`;
    },
    printDate() {
      const now = new Date();
      return now.toLocaleString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    },
    downloadFileName() {
      return `Hoory Conversation [${this.headerData.conversationId}] – [${this.headerData.client}]-${this.printDate}`;
    },
    formattedConversations() {
      return this.conversations.map(message => {
        const senderName = message?.sender ? message.sender : 'System message';

        const timestamp = message?.timestamp ? message.timestamp : 'N/A';

        let formattedContent = '';
        if (message.assistantType === 'rule-based') {
          formattedContent = this.botMessage(message);
        } else {
          formattedContent = message.content;
        }

        formattedContent = DOMPurify.sanitize(
          this.md.render(formattedContent),
          {
            ALLOWED_TAGS: [
              'p',
              'a',
              'img',
              'div',
              'span',
              'strong',
              'em',
              'br',
              'ul',
              'li',
              'i',
              'ol',
              'code',
            ],
            ALLOWED_ATTR: ['href', 'src', 'alt', 'class'],
          }
        );
        return {
          sender: senderName,
          timestamp,
          content: formattedContent,
          isPrivate: message.isPrivate,
          attachments: message.attachments || [],
          isBot: message.isBot,
        };
      });
    },
  },
  methods: {
    botMessage(message) {
      if (message.content === 'bot_form' && message.contentType === 'form') {
        const sv = message.contentAttributes.submitted_values;
        if (sv?.length > 0) {
          return `<strong class="tc">${sv[0].name}</strong><p>${sv[0].value}</p>`;
        }

        let name = '';
        let value;
        const input = message.contentAttributes.items[0];
        if (input.type === 'text_area') {
          name = 'long text';
          value = null;
        } else if (input.type === 'choice') {
          name = 'choice';
          value = input.items.map(i => i.content).join(', ');
        } else if (input.type === 'picture_choice') {
          name = 'picture choice';
          value = input.items.map(i => i.title).join(', ');
        } else {
          name = input.type;
          value = input.value;
        }
        return `<strong class="tc">${name} Input</strong><br/>${
          value ? `<p>${value}?</p>` : ``
        }<span class="not-answered">(Not Answered)</span>`;
      }

      const botMessageContent = generateBotMessageContent(
        message.contentType,
        message.contentAttributes,
        {
          noResponseText: this.$t('CONVERSATION.NO_RESPONSE'),
          csat: {
            ratingTitle: this.$t('CONVERSATION.RATING_TITLE'),
            feedbackTitle: this.$t('CONVERSATION.FEEDBACK_TITLE'),
          },
        }
      );

      if (message.contentType === 'input_csat') {
        return this.$t('CONVERSATION.CSAT_REPLY_MESSAGE') + botMessageContent;
      }

      if (message.contentType === 'require_action') {
        let action = '';
        try {
          const parsedAction = JSON.parse(message.content);
          action += `<pre>${JSON.stringify(parsedAction, null, 2)}</pre>`;
        } catch (e) {
          action += `<p>${message.content}</p>`;
        }

        return '<p>⚙️ Require Action</p>' + action;
      }
      return (
        this.formatMessage(message.content, false, message.isPrivate) +
        botMessageContent
      );
    },
    async preloadImages(content) {
      const div = document.createElement('div');
      div.style.display = 'none';
      div.innerHTML = content;
      document.body.appendChild(div);
      const images = div.getElementsByTagName('img');
      const promises = Array.from(images).map(
        img =>
          new Promise(resolve => {
            if (img.complete) {
              resolve();
            } else {
              img.onload = resolve;
              img.onerror = () => {
                resolve();
              };
            }
          })
      );
      await Promise.all(promises);
      document.body.removeChild(div);
    },
    async printCurrentView() {
      try {
        this.$emit('print-start', true);
        this.error = null;

        const preloadPromises = this.formattedConversations.map(msg => {
          const promises = [this.preloadImages(msg.content)];
          if (msg.attachments) {
            msg.attachments.forEach(attachment => {
              if (
                attachment.file_type === 'image' ||
                attachment.file_type === 'gif'
              ) {
                promises.push(
                  this.preloadImages(`<img src="${attachment.data_url}">`)
                );
              }
            });
          }
          return Promise.all(promises);
        });
        await Promise.all(preloadPromises);

        const printContent = this.$refs.printable;
        const width = 1200;
        const height = 800;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        const titleValue = this.downloadFileName;

        const printWindow = window.open(
          '',
          '',
          `width=${width},height=${height},left=${left},top=${top}`
        );
        printWindow.document.write(`
          <html>
            <head>
            <title>${titleValue}</title>
              <style>
        .chat-transcript { padding: 24px;  }
        .table-wrap{ border: 1px solid #DEDEDE; border-left: none; border-right: none; padding: 16px 0; margin-top: 6px !important; }
        .header {  padding: 10px; text-align: start; }
        .title { display: flex; flex-direction: column; justify-content: start; align-items: flex-start; gap: 8px; }
        .title p { margin: 0; color: #333; }
        .title-date { color: #333333; font-size: 14px; padding-bottom: 8px; }
        .url { margin-top: 15px; color: #666; }
        .url a { color: #949494; text-decoration: none; cursor: pointer; target: '_blank'; }
        table { width: 100%; border-collapse: collapse; margin: 0; border: none !important; }
        th, td { border: none !important; padding: 2px; text-align: left; font-size: 14px; color: #333; }
        th { border: none !important; }
        .messages-wrapper { margin-top: 33px !important; }
        .messages { padding: 12px 0; border-top: 1px dashed #DEDEDE; border-bottom: 1px dashed #DEDEDE; }
        .timestamp { font-weight: bold; margin: 0; color: #333; }
        .private-indicator { color: #404040; padding: 3px 9px; margin-left: 8px; border-radius: 8px; background: #FEE9D8 !important; font-size:12px }
        .content { margin: 5px 0 0; color: #666; font-size: 15px; }
        .content p { margin: 0; }
        .content a { color: #0066cc; text-decoration: underline; }
        .tc { text-transform: capitalize;}
        .content img { max-width: 200px; height: auto; display: block; margin: 5px 0; }
        .content-link { color: #0066cc; text-decoration: underline; }
        .not-answered { font-weight: var(--font-weight-medium); font-size: var(--font-size-mini); color: var(--s-300);
}
        .content-image { max-width: 200px; height: auto; display: block; margin: 5px 0; }
        .attachment-image { max-width: 200px; height: auto; display: block; margin: 5px 0; }
        .attachment-link { color: #0066cc; text-decoration: underline; display: block; margin: 5px 0; }
        .error { color: red; margin: 10px 0; }
        .date-style { color: #494949; font-size: 13px; }
        @page { margin: 0.5in; }
        @media print {
          @page { margin: 0; }
          html, body { margin: 0; padding: 5px; font-family: DM Sans, "Helvetica Neue", Helvetica, Arial, sans-serif; }
          .content-image, .attachment-image { max-width: 200px !important; max-height: 300px !important; }
          .private-indicator { background: #FEE9D8 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { @top-center { content: normal; } @bottom-center { content: normal; } }
        }
      </style>
            </head>
            <body>${printContent.outerHTML}</body>
          </html>
        `);
        printWindow.document.close();

        const images = printWindow.document.getElementsByTagName('img');
        let loadedImages = 0;
        const totalImages = images.length;

        if (totalImages === 0) {
          printWindow.focus();
          printWindow.print();
          setTimeout(() => {
            printWindow.close();
          }, 500);
          this.$emit('print-complete');
          this.$emit('print-start', false);
          return;
        }

        const onImageLoad = () => {
          loadedImages += 1;
          if (loadedImages === totalImages) {
            printWindow.focus();
            printWindow.print();
            setTimeout(() => {
              printWindow.close();
            }, 500);
            this.$emit('print-complete');
            this.$emit('print-start', false);
          }
        };

        Array.from(images).forEach(img => {
          if (img.complete) {
            onImageLoad();
          } else {
            img.onload = onImageLoad;
            img.onerror = () => {
              onImageLoad();
            };
          }
        });
      } catch (error) {
        this.error = `Failed to generate print preview: ${error.message}`;
        this.$emit('print-start', false);
        this.$emit('print-error', this.error);
      }
    },
  },
};
</script>
